import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Tooltip,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Edit,
  NotificationsOff,
  ContentCopy,
  Check,
  Comment,
  Close,
} from '@mui/icons-material';
import { useClientDetails, useActivities, useActiveReminder, useAddActivity, useUpdateCustomer, useSyncClientListLastContact, useSyncClientListCreatedBy, useSyncClientListReminder, useMarkReminderAsSeen, buildReminderClientState } from '../hooks/useClients';
import { buildStatusOptions, PRIORITY_COLORS, CRM_LABELS } from '../models/Client';
import {
  validateCustomerForm,
  buildFieldUpdatePayload,
  parseCustomerApiErrors,
  translateApiFieldMessage,
} from '../validation/customerValidation';
import ContactTimeline from './ContactTimeline';
import AddActivityDialog from './AddActivityDialog';
import CustomerFormDialog from './CustomerFormDialog';
import EditableDetailItem from './EditableDetailItem';
import LastContactHeaderField from './LastContactHeaderField';
import ReminderHeaderField from './ReminderHeaderField';
import ActiveReminderDialog from './ActiveReminderDialog';
import ColoredChipMenu from './ColoredChipMenu';

const STATUS_OPTIONS = buildStatusOptions('filled');

const PRIORITY_OPTIONS = Object.entries(CRM_LABELS.PRIORITY_LABELS).map(([value, label]) => ({
  value,
  label,
  color: PRIORITY_COLORS[value] || 'default',
  variant: 'outlined',
}));

const SECTION_ACCENTS = {
  'Dane firmy': 'primary',
  Adres: 'info',
  Kontakt: 'success',
  Notatki: 'secondary',
};

const DetailSection = ({ title, children, showDivider = true }) => {
  const accent = SECTION_ACCENTS[title] || 'primary';

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 0.75,
          mb: 0.5,
          borderRadius: 1,
          borderLeft: 3,
          borderColor: `${accent}.main`,
          bgcolor: `${accent}.50`,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.8125rem',
            color: `${accent}.dark`,
            letterSpacing: '0.02em',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ pl: 0.5 }}>{children}</Box>
      {showDivider && <Divider sx={{ my: 1.75 }} />}
    </Box>
  );
};

const ClientDetailsPanel = ({ clientId, onClose, variant = 'default' }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityVariant, setActivityVariant] = useState('activity');
  const [invoiceCopied, setInvoiceCopied] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [inlineError, setInlineError] = useState('');
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);

  const { data: client, isLoading, error } = useClientDetails(clientId, true);
  const {
    data: activities,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useActivities(clientId, true);
  const { data: activeReminder } = useActiveReminder(clientId, true);

  const addActivityMutation = useAddActivity(clientId);
  const updateCustomerMutation = useUpdateCustomer(clientId);
  const markReminderMutation = useMarkReminderAsSeen();

  const latestActivityDate = useMemo(() => {
    if (!activities?.length) return null;
    return activities[0].createdAt || null;
  }, [activities]);

  const reminderState = useMemo(
    () => (client ? buildReminderClientState(client, activeReminder) : null),
    [client, activeReminder]
  );

  const reminderDialogError =
    markReminderMutation.error?.response?.data?.message ||
    markReminderMutation.error?.message;

  useSyncClientListLastContact(clientId, client?.lastContactDate, latestActivityDate);
  useSyncClientListCreatedBy(clientId, client?.createdBy);
  useSyncClientListReminder(clientId, activeReminder);

  const activityError =
    addActivityMutation.error?.response?.data?.message ||
    addActivityMutation.error?.message;

  const updateError = updateCustomerMutation.error;

  const handleOpenAddReminder = () => {
    markReminderMutation.reset();
    setReminderDialogOpen(false);
    setActivityVariant('activity');
    setActivityOpen(true);
  };

  const handleOpenManageReminder = () => {
    markReminderMutation.reset();
    setReminderDialogOpen(true);
  };

  const handleCloseReminderDialog = () => {
    if (!markReminderMutation.isPending) {
      setReminderDialogOpen(false);
      markReminderMutation.reset();
    }
  };

  const handleDismissReminder = async () => {
    if (!reminderState?.reminderId) return;

    try {
      await markReminderMutation.mutateAsync({
        reminderId: reminderState.reminderId,
        clientId: reminderState.id,
      });
      setReminderDialogOpen(false);
    } catch {
      // error shown in dialog
    }
  };

  const handleDismissReminderFromHeader = async (event) => {
    event?.stopPropagation?.();
    await handleDismissReminder();
  };

  const handleReminderBellClick = (event) => {
    event.stopPropagation();
    if (reminderState?.hasActiveReminder) {
      handleDismissReminderFromHeader(event);
    } else {
      handleOpenAddReminder();
    }
  };

  const handleCloseEdit = () => {
    updateCustomerMutation.reset();
    setEditOpen(false);
  };

  const handleOpenEdit = (e) => {
    e.stopPropagation();
    setEditingField(null);
    setInlineError('');
    setEditOpen(true);
  };

  const handleFieldSave = async (field, newValue) => {
    if (!client) return { success: false, error: 'Brak danych klienta.' };

    const originalForm = client.toFormData();
    const nextForm = { ...originalForm, [field]: newValue };
    const payload = buildFieldUpdatePayload(field, newValue, originalForm);

    if (Object.keys(payload).length === 0) {
      setEditingField(null);
      return { success: true };
    }

    const validation = await validateCustomerForm(nextForm, {
      mode: 'edit',
      fields: [field],
    });

    if (!validation.isValid) {
      return {
        success: false,
        error: validation.fieldErrors[field] || validation.generalError || 'Niepoprawna wartość.',
      };
    }

    setSavingField(field);
    setInlineError('');

    try {
      await updateCustomerMutation.mutateAsync(payload);
      updateCustomerMutation.reset();
      setEditingField(null);
      return { success: true };
    } catch (submitError) {
      const { fieldErrors, generalError } = parseCustomerApiErrors(submitError);
      const message = translateApiFieldMessage(fieldErrors[field] || generalError || '');
      setInlineError(message);
      return { success: false, error: message };
    } finally {
      setSavingField(null);
    }
  };

  const handleCopyInvoiceData = async (e) => {
    e.stopPropagation();
    if (!client) return;

    const text = client.getInvoiceCopyText();
    if (!text.trim()) return;

    try {
      await navigator.clipboard.writeText(text);
      setInvoiceCopied(true);
      setTimeout(() => setInvoiceCopied(false), 2000);
    } catch (err) {
      console.error('Błąd podczas kopiowania:', err);
    }
  };

  const editableProps = {
    editingField,
    savingField,
    onStartEdit: (field) => {
      setInlineError('');
      setEditingField(field);
    },
    onCancelEdit: () => setEditingField(null),
    onSave: handleFieldSave,
    editable: !editOpen,
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Nie udało się załadować szczegółów klienta.
      </Alert>
    );
  }

  if (!client) return null;

  const hasInvoiceData = Boolean(client.getInvoiceCopyText().trim());
  const formData = client.toFormData();
  const isDrawer = variant === 'drawer';
  const fieldProps = { ...editableProps, compact: isDrawer };

  return (
    <Box
      sx={{
        bgcolor: isDrawer ? 'background.paper' : 'grey.50',
        minHeight: isDrawer ? '100%' : undefined,
      }}
    >
      <Box
        sx={{
          position: isDrawer ? 'sticky' : 'static',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderBottom: isDrawer ? 1 : 0,
          borderColor: 'divider',
          px: isDrawer ? 2 : { xs: 2, sm: 3 },
          py: isDrawer ? 1.5 : { xs: 2, sm: 3 },
          mb: isDrawer ? 0 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          {onClose && (
            <IconButton size="small" onClick={onClose} aria-label="Zamknij panel" sx={{ mt: 0.25 }}>
              <Close fontSize="small" />
            </IconButton>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant={isDrawer ? 'subtitle1' : 'subtitle1'}
              sx={{ fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}
            >
              {isDrawer ? client.companyName : 'Szczegóły klienta'}
            </Typography>

            {isDrawer && client.city && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {client.city}
              </Typography>
            )}

            {isDrawer && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                <ColoredChipMenu
                  value={client.status}
                  label={client.getStatusLabel()}
                  options={STATUS_OPTIONS}
                  onChange={(newStatus) => handleFieldSave('status', newStatus)}
                  disabled={editOpen}
                  isSaving={savingField === 'status'}
                  chipVariant="filled"
                  tooltip="Kliknij, aby zmienić status"
                />
                <ColoredChipMenu
                  value={client.priority}
                  label={client.getPriorityLabel()}
                  options={PRIORITY_OPTIONS}
                  onChange={(newPriority) => handleFieldSave('priority', newPriority)}
                  disabled={editOpen}
                  isSaving={savingField === 'priority'}
                  chipVariant="outlined"
                  tooltip="Kliknij, aby zmienić priorytet"
                />
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              flexShrink: 0,
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                justifyContent: 'flex-end',
              }}
            >
            {isDrawer ? (
              <>
                <Tooltip title={invoiceCopied ? 'Skopiowano!' : 'Dane do faktury'}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!hasInvoiceData}
                      color={invoiceCopied ? 'success' : 'default'}
                      onClick={handleCopyInvoiceData}
                    >
                      {invoiceCopied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    reminderState?.hasActiveReminder
                      ? 'Wyłącz powiadomienie'
                      : 'Dodaj przypomnienie'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleReminderBellClick}
                      disabled={markReminderMutation.isPending}
                      color={reminderState?.hasActiveReminder ? 'error' : 'default'}
                      sx={{
                        opacity: reminderState?.hasActiveReminder ? 1 : 0.55,
                      }}
                    >
                      {markReminderMutation.isPending ? (
                        <CircularProgress size={16} />
                      ) : (
                        <NotificationsOff fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Edytuj wszystko">
                  <IconButton size="small" onClick={handleOpenEdit}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title={invoiceCopied ? 'Skopiowano!' : 'Kopiuj dane do faktury'}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasInvoiceData}
                      startIcon={invoiceCopied ? <Check /> : <ContentCopy />}
                      color={invoiceCopied ? 'success' : 'primary'}
                      onClick={handleCopyInvoiceData}
                    >
                      {invoiceCopied ? 'Skopiowano' : 'Dane do faktury'}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  color={reminderState?.hasActiveReminder ? 'error' : 'primary'}
                  startIcon={<NotificationsOff />}
                  onClick={handleReminderBellClick}
                  disabled={markReminderMutation.isPending}
                >
                  {reminderState?.hasActiveReminder ? 'Wyłącz powiadomienie' : 'Dodaj przypomnienie'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={handleOpenEdit}
                >
                  Edytuj
                </Button>
              </>
            )}
            </Box>
            {isDrawer && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 0.5,
                }}
              >
                <LastContactHeaderField
                  manualDate={client.lastContactDate}
                  latestActivityDate={latestActivityDate}
                  editingField={editingField}
                  savingField={savingField}
                  onStartEdit={(field) => {
                    setInlineError('');
                    setEditingField(field);
                  }}
                  onCancelEdit={() => setEditingField(null)}
                  onSave={handleFieldSave}
                  editable={!editOpen}
                />
                <ReminderHeaderField
                  hasActiveReminder={reminderState?.hasActiveReminder}
                  remindAt={reminderState?.remindAt}
                  editable={!editOpen}
                  onAddReminder={handleOpenAddReminder}
                  onManageReminder={handleOpenManageReminder}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: isDrawer ? 2 : { xs: 2, sm: 3 }, pt: isDrawer ? 1.5 : 0 }}>
        <Stack spacing={0}>
          <DetailSection title="Dane firmy">
            <EditableDetailItem
              field="companyName"
              label="Nazwa firmy"
              value={formData.companyName}
              {...fieldProps}
            />
            <EditableDetailItem
              field="nip"
              label="NIP"
              value={formData.nip}
              {...fieldProps}
            />
          </DetailSection>

          <DetailSection title="Adres">
            <EditableDetailItem
              field="address"
              label="Ulica i numer"
              value={formData.address}
              {...fieldProps}
            />
            <EditableDetailItem
              field="postalCode"
              label="Kod pocztowy"
              value={formData.postalCode}
              {...fieldProps}
            />
            <EditableDetailItem
              field="city"
              label="Miasto"
              value={formData.city}
              {...fieldProps}
            />
          </DetailSection>

          <DetailSection title="Kontakt">
            <EditableDetailItem
              field="contactPerson"
              label="Osoba kontaktowa"
              value={formData.contactPerson}
              {...fieldProps}
            />
            <EditableDetailItem
              field="phone"
              label="Telefon"
              value={formData.phone}
              {...fieldProps}
            />
            <EditableDetailItem
              field="email"
              label="E-mail"
              value={formData.email}
              inputType="email"
              {...fieldProps}
            />
          </DetailSection>

          <DetailSection title="Notatki" showDivider={false}>
            <EditableDetailItem
              field="notes"
              label="Treść"
              value={formData.notes}
              inputType="multiline"
              {...fieldProps}
            />
          </DetailSection>
        </Stack>

      {(inlineError || (updateError && !editOpen && !editingField)) && (
        <Alert severity="error" sx={{ mt: 2 }} onClick={(e) => e.stopPropagation()}>
          {inlineError || 'Nie udało się zapisać zmian klienta.'}
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          px: isDrawer ? 0 : 0,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 600,
            fontSize: '0.6875rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Historia kontaktu
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Comment />}
          onClick={(e) => {
            e.stopPropagation();
            setActivityVariant('comment');
            setActivityOpen(true);
          }}
        >
          Dodaj komentarz
        </Button>
      </Box>

      <ContactTimeline
        activities={activities}
        isLoading={activitiesLoading}
        error={activitiesError}
      />

      <AddActivityDialog
        variant={activityVariant}
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        onSubmit={(data) => addActivityMutation.mutateAsync(data)}
        isPending={addActivityMutation.isPending}
        error={activityError}
      />

      <ActiveReminderDialog
        open={reminderDialogOpen}
        client={reminderState}
        onClose={handleCloseReminderDialog}
        onDismiss={handleDismissReminder}
        isPending={markReminderMutation.isPending}
        error={reminderDialogError}
        onAddNew={handleOpenAddReminder}
      />

      <CustomerFormDialog
        mode="edit"
        open={editOpen}
        onClose={handleCloseEdit}
        initialData={formData}
        onSubmit={(data) => updateCustomerMutation.mutateAsync(data)}
        isPending={updateCustomerMutation.isPending}
      />
      </Box>
    </Box>
  );
};

export default ClientDetailsPanel;
